// --- 数据资产域公共枚举(波次4 附录 A/B;领域字段清单以 08-数据资产组设计 §3 为准) ---

/**
 * 数据资产生命周期状态(波次4 附录 A)
 *
 * draft → building(首次接收批次成功后自动)→ publishable(首个版本发布后自动)→
 * published(每次 currentVersion 切换);suspended 停止新发布;archived 终态数据只读。
 */
export enum DataAssetStatusEnum {
  Draft = "draft",
  Building = "building",
  Publishable = "publishable",
  Published = "published",
  Suspended = "suspended",
  Archived = "archived",
}

/**
 * 接收批次状态(波次4 附录 A)
 *
 * 两层校验分工:API 层 checksum/模型版本校验失败 → 400 拒绝,不创建批次;
 * 进入 receiving 后预分析再校验失败(包缺失/包内容与快照不符)→ failed 终态,可 resubmit。
 */
export enum IngestionBatchStatusEnum {
  Receiving = "receiving",
  PreAnalyzed = "preAnalyzed",
  Reviewing = "reviewing",
  Applied = "applied",
  Returned = "returned",
  Failed = "failed",
  Cancelled = "cancelled",
}

/**
 * 变更集状态(发布即快照:提交时点钉死发布清单并进入审批,驳回回草稿)
 */
export enum ChangeSetStatusEnum {
  Draft = "draft",
  Publishing = "publishing",
  Released = "released",
  Discarded = "discarded",
}

/**
 * 实体实例状态
 */
export enum EntityInstanceStatusEnum {
  Active = "active",
  Merged = "merged",
  Retired = "retired",
}

/**
 * 事实修订状态:effective 生效中;superseded 已被新修订取代(保留历史)
 */
export enum FactRevisionStatusEnum {
  Effective = "effective",
  Superseded = "superseded",
}

/**
 * 关系实例状态(生效/失效由版本边界表达)
 */
export enum RelationInstanceStatusEnum {
  Active = "active",
  Invalidated = "invalidated",
}

/**
 * 多对象变更操作状态(合并/拆分/纠错;3C-4 同构协议)
 */
export enum AssetMutationStatusEnum {
  Running = "running",
  Committed = "committed",
  RolledBack = "rolledBack",
}

/**
 * 审核项处理状态(4B-3)
 */
export enum ReviewItemStatusEnum {
  Pending = "pending",
  Resolved = "resolved",
}

/**
 * 身份匹配四分类(决策 7)
 */
export enum IdentityMatchResultEnum {
  Exact = "exact",
  Possible = "possible",
  Conflict = "conflict",
  NewEntity = "newEntity",
}

/**
 * 身份键类型(决策 7)
 */
export enum IdentityKeyTypeEnum {
  Primary = "primary",
  Candidate = "candidate",
  Composite = "composite",
}

/**
 * 身份匹配策略:首期仅精确/归一化精确;
 * 成分指纹/条件指纹为 08 DA-04 预留枚举位(本波次不实现)
 */
export enum IdentityMatchStrategyEnum {
  Exact = "exact",
  NormalizedExact = "normalizedExact",
  /** 预留:成分指纹 */
  CompositionFingerprint = "compositionFingerprint",
  /** 预留:条件指纹 */
  ConditionFingerprint = "conditionFingerprint",
}

/**
 * 身份冲突策略
 */
export enum IdentityConflictPolicyEnum {
  /** 阻止重复 */
  Block = "block",
  /** 允许并存 */
  Coexist = "coexist",
  /** 人工合并 */
  ManualMerge = "manualMerge",
}

/**
 * 资产域可靠事件类型(决策 9;消费方 = 视图物化 worker 与后续知识服务)
 */
export enum AssetOutboxEventEnum {
  VersionPublished = "asset.version.published",
  VersionRolledBack = "asset.version.rolledBack",
  ViewRebuildRequested = "asset.view.rebuildRequested",
}

/**
 * 视图类型(决策 13;GraphProjectionDefinition 归波次 5,不在本枚举)
 */
export enum AssetViewTypeEnum {
  EntityTable = "entityTable",
  MasterDetail = "masterDetail",
  WideTable = "wideTable",
  StatsTable = "statsTable",
}

/**
 * 物化状态
 */
export enum ViewMaterializeStatusEnum {
  Pending = "pending",
  Building = "building",
  Built = "built",
  Failed = "failed",
}

/**
 * 审核队列七类(4B-3)
 */
export enum ReviewQueueTypeEnum {
  NewEntity = "newEntity",
  ExistingUpdate = "existingUpdate",
  IdentityConflict = "identityConflict",
  ValueConflict = "valueConflict",
  RelationConflict = "relationConflict",
  MissingEvidence = "missingEvidence",
  RuleFailure = "ruleFailure",
}

/**
 * 查询结果数据状态(决策 3 查询契约;已合并经 redirect 展示)
 */
export enum AssetDataStatusEnum {
  Official = "official",
  PendingUpdate = "pendingUpdate",
  Invalidated = "invalidated",
  Merged = "merged",
}

/**
 * 审核操作类型(4B-3)
 */
export enum ReviewActionEnum {
  Accept = "accept",
  Reject = "reject",
  Modify = "modify",
  KeepMultiple = "keepMultiple",
  BindEntity = "bindEntity",
}

/**
 * 资产创建来源(4A-1 三入口:新建/从模型创建/从旧数据集升级建壳占位)
 */
export enum AssetOriginEnum {
  Manual = "manual",
  FromModel = "fromModel",
  LegacyUpgrade = "legacyUpgrade",
}

/**
 * 查询过滤比较符(4C-2 查询契约)
 */
export enum QueryFilterOpEnum {
  Eq = "eq",
  Ne = "ne",
  Gt = "gt",
  Gte = "gte",
  Lt = "lt",
  Lte = "lte",
  In = "in",
  Between = "between",
  Contains = "contains",
}

/**
 * 治理任务类型(26 号 P1 治理工作台:入库后资产级候选发现,不塞强绑 batchId 的审核项)
 */
export enum GovernanceTaskKindEnum {
  /** 同名候选:同实体类型、名称归一化后相同的多个活动实体 */
  SameName = "sameName",
  /** 别名候选:某实体别名命中另一实体名称 */
  AliasMatch = "aliasMatch",
  /** 悬空端点:活动关系的端点实体已合并/退役/不存在 */
  DanglingEndpoint = "danglingEndpoint",
  /** 孤点:无任何活动关系的实体 */
  IsolatedEntity = "isolatedEntity",
  /** 缺证据:实体有效事实无任何证据引用 */
  MissingEvidence = "missingEvidence",
  /** 核证发现(26 号 P3):LLM 逐实体/边审读后的可操作项(未支持/属性冲突/可补属性/身份疑点) */
  Verification = "verification",
}

export enum GovernanceTaskStatusEnum {
  Pending = "pending",
  Decided = "decided",
  Applied = "applied",
  Dismissed = "dismissed",
  Failed = "failed",
}

/**
 * 治理裁决动作:merge=并入目标实体;keepSeparate=确认各自独立;retireRelation=关系失效;
 * retireEntity=实体退役;dismiss=忽略本任务
 */
export enum GovernanceDecisionActionEnum {
  Merge = "merge",
  KeepSeparate = "keepSeparate",
  RetireRelation = "retireRelation",
  RetireEntity = "retireEntity",
  Dismiss = "dismiss",
  /** 核证(P3):采纳建议属性为事实修订(带段落证据) */
  AdoptFacts = "adoptFacts",
  /** 核证(P3):人工确认 LLM 判定(实体/边标记为已核证) */
  MarkVerified = "markVerified",
}

/** 核证标记状态(实体/边 verification.status;noPassage=语料中未定位到段落) */
export enum VerificationStatusEnum {
  Supported = "supported",
  Unsupported = "unsupported",
  Doubt = "doubt",
  NoPassage = "noPassage",
}

/**
 * 资产语料集引用类型(26 号 P2 来源域):file=文件中心文件(DocCenterFile);kb=知识库(Kb)。
 * 引用一律绑不可变 id,名称仅展示缓存。
 */
export enum AssetCorpusRefKindEnum {
  File = "file",
  Kb = "kb",
}
